import { debounce } from 'lodash'
import moment from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import Select from '../../../../components/textFields/AppSelect'
import { components } from 'react-select'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { KTSVG } from '../../../../_metronic/helpers'
import { PageTitle } from '../../../../_metronic/layout/core'
import CurrencyComp from '../../../../components/currencyComponent/CurrencyComp'
import DateComp from '../../../../components/dateComponent/DateComp'
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp'
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../../components/noRecord/NoRecordFound'
import Pagination from '../../../../components/pagination/Pagination'
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent'
import SearchBarComponent from '../../../../components/searchBar/SearchBarComponent'
import TableHeading from '../../../../components/tableHeading/TableHeading'
import TableSettingMenu from '../../../../components/tableHeading/TableSettingMenu'
import SearchSelect from '../../../../components/textFields/SearchSelect'
import TextField from '../../../../components/textFields/TextField'
import { constraintConfig } from '../../../../constraintConfig'
import { useGetReportExpenseStatusQuery, useGetAllUsersManagerAdminQuery } from '../../../../services/GeneralApi'
import { useGetBCSyncDataQuery, useLazyGetBCSyncDatatExcelExportReportQuery } from '../../../../services/ReportAndAnalyticsApi'
import useColumnVisibility from '../../../customHooks/useColumnVisibility'
import labelKey from '../../../localization/label.json'
import tosterKey from '../../../localization/toster.json'
import { Alert } from 'react-bootstrap';

const { Option } = components

type Option = {
  value: string
  label: string
}

// Custom Option component to add checkboxes
const CheckboxOption = (props: any) => {
  return (
    <Option {...props} className='d-flex align-items-start gap-2'>
      <input type='checkbox' checked={props.isSelected} onChange={() => null} />{' '}
      <label>{props.label}</label>
    </Option>
  )
}

// Table column configuration
interface TableColumn {
  id: string
  label: string
  type?: 'currency' | 'date' | 'link' | 'text' | 'boolean'
  className?: string
  labelClassName?: string
  sortable?: boolean
  linkConfig?: {
    urlKey: string
    textKey?: string
  }
}

const tableColumns: TableColumn[] = [
  { id: 'bcEmployeeId', label: 'Bc Employee Id', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', sortable: true },
  { id: 'expenseId', label: 'SS Expense Id', className: 'ps-4 text-center', sortable: true },
  { id: 'trackingId', label: 'SS Tracking Id', className: 'ps-4 text-center', sortable: true },
  { id: 'expenseStatus', label: 'SS Expense Status', className: 'ps-4 text-center', sortable: true },
  { id: 'ssReportTotalAmount', label: 'SS Report Total Amount', labelClassName: 'text-end', className: 'text-end', sortable: true },
  { id: 'bcSyncReportTotalAmount', label: 'BC Sync Report Total Amount', labelClassName: 'text-primary text-end', className: 'text-end', sortable: true },
  { id: 'bcExpenseCreated', label: 'BC Expense Created', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', type: 'boolean', sortable: true },
  { id: 'bcExpenseCreatedDateTime', label: 'BC Expense Created Date', labelClassName: 'text-primary text-center', type: 'date', className: 'w-100px text-center', sortable: true },
  { id: 'bcExpenseCreatedMsg', label: 'BC Expense Created Msg', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'bcInvoiceOrCreditMemoNo', label: 'BC Invoice Number', labelClassName: 'text-primary text-center', className: 'ps-4 text-center', sortable: true },
  { id: 'bcRespPayToVendorNumber', label: 'BC Pay To Vendor No', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'vendorName', label: 'BC Pay To Vendor Name', labelClassName: 'text-primary text-center', className: 'text-center', sortable: true },
  { id: 'expenseCategory', label: 'SS Expense Category', className: 'ps-4 text-center', sortable: true },
  { id: 'paymentMethod', label: 'SS Payment Method', className: 'ps-4 text-center', sortable: true },
  { id: 'itemDate', label: 'SS Item Date', className: 'ps-4 text-center', sortable: true, type: 'date' },
  { id: 'itemAmount', label: 'SS Item Amount', className: 'ps-4 text-center', sortable: true },
]

const ReportBankAndBCDataMatch = () => {
  const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'reportBankAndBCDataMatch' })

  const currentDate = moment().format('MM/DD/YYYY')
  const sevenDaysAgo = moment().subtract(6, 'days').format('MM/DD/YYYY')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchStr, setSearchStr] = useState('')
  const [reportId, setReportId] = useState('')
  const [immediateSearchStr, setImmediateSearchStr] = useState('')
  const [filterByBCEmployeeId, setFilterByBCEmployeeId] = useState('')
  const [localFilterByBCEmployeeId, setLocalFilterByBCEmployeeId] = useState('')
  const [filterByReportStatus, setFilterByReportStatus] = useState('')
  const [localFilterByReportStatus, setLocalFilterByReportStatus] = useState('')
  const [sortBy, setSortBy] = useState('trackingId')
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortedColumn, setSortedColumn] = useState('trackingId')
  const [filterByStartDate, setFilterByStartDate] = useState(sevenDaysAgo)
  const [filterByEndDate, setFilterByEndDate] = useState(currentDate)
  const [localFilterByStartDate, setLocalFilterByStartDate] = useState(sevenDaysAgo)
  const [localFilterByEndDate, setLocalFilterByEndDate] = useState(currentDate)
  const [filterByUserIds, setFilterByUserIds] = useState<string[]>([])
  const [localFilterByUserIds, setLocalFilterByUserIds] = useState<string[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<Option[]>([])
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [excelReportTrigger, { data: excelReportData, isFetching: excelReportIsFecting }] =
    useLazyGetBCSyncDatatExcelExportReportQuery({});
  const [downloading, setDownloading] = useState(false)
  const handleDownload = async () => {
    setDownloading(true)
    try {
      const blob = await excelReportTrigger({
        userIds: filterByUserIds.length > 0 ? filterByUserIds.join(',') : '""',
        filterByHideExpenseForUserManager: false,
        pageSize: pageSize,
        pageNumber: pageNumber,
        searchStr: searchStr,
        reportId: reportId,
        bcEmployeeId: filterByBCEmployeeId,
        reportStatusId: filterByReportStatus,
        startDate: filterByStartDate,
        endDate: filterByEndDate,
        sortBy: sortBy,
        sortOrder: sortOrder,
      }).unwrap()

      if (blob) {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'BankAndBCDataMatch_Report.xlsx' // Set the file name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Download failed:', error)
    } finally {
      setDownloading(false)
    }
  }

  // Fetch API data
  const { data, isFetching, refetch, isSuccess } = useGetBCSyncDataQuery({
    userIds: filterByUserIds.length > 0 ? filterByUserIds.join(',') : '""',
    filterByHideExpenseForUserManager: false,
    pageSize: pageSize,
    pageNumber: pageNumber,
    searchStr: searchStr,
    reportId: reportId,
    bcEmployeeId: filterByBCEmployeeId,
    reportStatusId: filterByReportStatus,
    startDate: filterByStartDate,
    endDate: filterByEndDate,
    sortBy: sortBy,
    sortOrder: sortOrder,
  })

  const { data: reportStatusLookup } = useGetReportExpenseStatusQuery('')
  const { data: getUserByManager } = useGetAllUsersManagerAdminQuery('')

  // Handle page size change
  const handlePageSizeChange = (e: any) => {
    const newSize = parseInt(e.target.value)
    setPageSize(newSize)
    setPageNumber(1)
  }

  // Pagination
  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1)
  }

  // Sort handler
  const handleSort = (property: string) => {
    setSortBy(property)
    if (sortedColumn === property) {
      setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortedColumn(property)
      setSortOrder('asc')
    }
  }

  // Search handlers
  const handleSearchChange = (e: any) => {
    const { value } = e.target
    setImmediateSearchStr(value)
    delayedSearch(value)
    setPageNumber(1)
  }
  // Search handlers
  const handleReportIdChange = (e: any) => {
    const { value } = e.target
    setReportId(value)
  }

  const delayedSearch = useCallback(
    debounce((searchValue) => {
      setSearchStr(searchValue)
    }, 500),
    []
  )

  const handleBCEmployeeIdChange = (e: any) => {
    const { value } = e.target
    setLocalFilterByBCEmployeeId(value)
  }

  const handleReportStatusChange = (e: any) => {
    setLocalFilterByReportStatus(e?.value || '')
  }

  const userIdsOptions: Option[] = getUserByManager?.result?.map((userByManager: any) => ({
    value: userByManager.id,
    label: userByManager.value,
  })) || []

  const handleUserIdsChange = (selected: Option[]) => {
    setSelectedUserIds(selected)
    setLocalFilterByUserIds(selected?.map((userByManager) => userByManager.value))
  }

  // Date range handler
  const handleDateRangeChange = (event: any, picker: any) => {
    const startDateFormatted = moment(picker.startDate).format('MM/DD/YYYY')
    const endDateFormatted = moment(picker.endDate).format('MM/DD/YYYY')

    setLocalFilterByStartDate(startDateFormatted)
    setLocalFilterByEndDate(endDateFormatted)
  }

  const handleCancelDateSelection = (event: any, picker: any) => {
    setLocalFilterByStartDate('')
    setLocalFilterByEndDate('')
  }

  // Apply filters
  const applyFilters = () => {
    setIsApplyingFilters(true)
    setFilterByStartDate(localFilterByStartDate)
    setFilterByEndDate(localFilterByEndDate)
    setFilterByBCEmployeeId(localFilterByBCEmployeeId)
    setFilterByReportStatus(localFilterByReportStatus)
    setFilterByUserIds(localFilterByUserIds)
    setPageNumber(1)
    setTimeout(() => setIsApplyingFilters(false), 500)
  }

  // Reset filters
  const resetFilters = () => {
    setFilterByStartDate('')
    setFilterByEndDate('')
    setFilterByBCEmployeeId('')
    setFilterByReportStatus('')
    setFilterByUserIds([])
    setSearchStr('')
    setReportId('')
    setImmediateSearchStr('')
    setLocalFilterByStartDate('')
    setLocalFilterByEndDate('')
    setLocalFilterByBCEmployeeId('')
    setLocalFilterByReportStatus('')
    setLocalFilterByUserIds([])
    setSelectedUserIds([])
    setPageNumber(1)
  }

  // Refresh handler
  const handleRefresh = () => {
    refetch().then(() => {
      if (isSuccess) {
        toast.success(tosterKey.dataRefreshedSuccesfully)
      }
    })
  }

  // Loading effect
  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  // Trigger refetch when filters change
  useEffect(() => {
    if (reportId !== '' || searchStr !== '' || filterByBCEmployeeId !== '' || filterByReportStatus !== '' || filterByUserIds.length > 0) {
      refetch()
    }
  }, [reportId, searchStr, filterByBCEmployeeId, filterByReportStatus, filterByUserIds, refetch])

  const reportStatusOptions: Option[] = reportStatusLookup?.result
    ? reportStatusLookup.result.map((status: any) => ({
      value: status.id,
      label: status.value,
    }))
    : []

  const tableData = data?.result?.data || []
  const totalRecords = data?.result?.totalRecords || 0

  // Helper function to render cell content based on column type
  const renderCellContent = (item: any, column: TableColumn) => {
    const value = item[column.id]

    if (!value && value !== 0) return '-'

    switch (column.type) {
      case 'currency':
        return <CurrencyComp amount={value} />
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return <DateComp formattedDate={value} />
      case 'link':
        if (column.linkConfig && value) {
          const linkText = column.linkConfig.textKey || value
          return (
            <a
              href={value}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary'
            >
              {linkText}
            </a>
          )
        }
        return value
      default:
        return value
    }
  }

  // Helper function to render cell with proper styling
  const renderCell = (item: any, column: TableColumn) => {
    const isCurrency = column.type === 'currency'
    const isDate = column.type === 'date'

    if (isCurrency) {
      return (
        <td className='text-end pe-4'>
          {renderCellContent(item, column)}
        </td>
      )
    }

    if (isDate) {
      return (
        <td className='text-center'>
          {renderCellContent(item, column)}
        </td>
      )
    }

    return (
      <td className={column.className || 'ps-4 text-center'}>
        {renderCellContent(item, column)}
      </td>
    )
  }

  // Get visible columns based on hiddenColumns
  const visibleColumns = tableColumns.filter(col => hiddenColumns.includes(col.id))

  return (
    <>
      <PageTitle breadcrumbs={[]}>SS To BC Sync Items Detail</PageTitle>
      <Tooltip id='refreshData' place='top' />

      <div className='card ss-analytics-report-card mb-5'>
        <div className='card-body pb-8 pt-5'>
          <div className='row mb-2'>
            <div className='col-10'>
              <Alert variant="primary" className='w-100 py-2 mb-2'>
                <p className='mb-0 fs-7'>
                  View SS and BC synced data based on your selected filters below. You can also export this data as a .xlsx file by clicking the "Export Report" button.
                </p>
              </Alert>
            </div>
            <div className='col-2'>
              <div className='d-flex align-items-center gap-2 flex-row-reverse'>
                <RefreshComponent onClick={handleRefresh} />
                <button
                  type='button'
                  className='btn btn-sm btn-info '
                  disabled={downloading || excelReportIsFecting}
                  onClick={handleDownload}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                  {downloading || excelReportIsFecting
                    ? 'Exporting Report'
                    : 'Export Report'}
                </button>
              </div>
            </div>
          </div>
          {/* Filter Row */}
          <div className='row align-items-end'>
            {/* <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <label className='form-label fs-6 fw-bold'>General Search:</label>
              <SearchBarComponent
                placeholder=''
                value={immediateSearchStr}
                onChange={handleSearchChange}
                className='w-100'
              />
            </div> */}
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <label className='form-label fs-6 fw-bold'>Report ID:</label>
              <SearchBarComponent
                placeholder=''
                value={reportId}
                onChange={handleReportIdChange}
                className='w-100'
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <label className='form-label fs-6 fw-bold'>{labelKey.bcEmployeeId}:</label>
              <SearchBarComponent
                placeholder='BC Employee ID'
                value={localFilterByBCEmployeeId}
                onChange={handleBCEmployeeIdChange}
                className='w-100'
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <label className='form-label fs-6 fw-bold'>{labelKey.users}:</label>
              <Select
                isMulti
                options={userIdsOptions}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: CheckboxOption }}
                onChange={(selected) => handleUserIdsChange(selected as Option[])}
                value={selectedUserIds}
                placeholder='Search User...'
                classNamePrefix='react-select'
                className='filter-valueContainer'
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <SearchSelect
                label={`${labelKey.reportStatus}:`}
                options={reportStatusOptions}
                placeholder={labelKey.selectStatus}
                value={
                  localFilterByReportStatus && localFilterByReportStatus !== ''
                    ? {
                      value: localFilterByReportStatus,
                      label: reportStatusOptions.find(
                        (option) => option.value === localFilterByReportStatus
                      )?.label || '',
                    }
                    : null
                }
                onChange={handleReportStatusChange}
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <DateRangeComp
                label='Report Date Range'
                startDate={localFilterByStartDate || moment()}
                endDate={localFilterByEndDate || moment()}
                onApply={handleDateRangeChange}
                onCancel={handleCancelDateSelection}
                value={
                  localFilterByStartDate && localFilterByEndDate
                    ? `${localFilterByStartDate} - ${localFilterByEndDate}`
                    : 'MM/DD/YYYY'
                }
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5'>
              <div className='d-flex align-items-end h-100 gap-3'>
                <button className='btn btn-danger pull-right me-3' onClick={resetFilters}>
                  Reset Filters
                </button>
                <button className='btn btn-primary pull-right' onClick={applyFilters}>
                  {isApplyingFilters ? (
                    <>
                      <span>loading...</span>
                      <Spinner
                        as='span'
                        animation='border'
                        size='sm'
                        role='status'
                        aria-hidden='true'
                      />
                    </>
                  ) : (
                    'Apply Filters'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className='table-responsive table-height'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    {visibleColumns.map((column) => (
                      <TableHeading
                        key={column.id}
                        label={column.label}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId={column.id}
                        className={column.className}
                        labelClassName={column.labelClassName}
                      />
                    ))}
                    <th className='text-end rounded-end pe-2'>
                      <TableSettingMenu>
                        {tableColumns.map((column) => (
                          <TextField
                            key={column.id}
                            rightLabel={column.label}
                            type='checkbox'
                            onChange={() => handleToggleColumn(column.id)}
                            checked={hiddenColumns.includes(column.id)}
                            fieldClass='mb-4'
                          />
                        ))}
                      </TableSettingMenu>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading && (
                    <>
                      {tableData.length > 0 ? (
                        <>
                          {tableData.map((item: any, index: any) => (
                            <tr key={index}>
                              {visibleColumns.map((column) => renderCell(item, column))}
                            </tr>
                          ))}
                        </>
                      ) : (
                        <tr>
                          <td colSpan={visibleColumns.length + 1}>
                            <NoRecordFound />
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                  {isLoading && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className='text-center'>
                        <LoadingComponent />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalResult={totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1}
              toResult={totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)}
              ofResult={totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.result?.totalPages || 0}
              currentPage={pageNumber}
            />
        </div>
      </div>
    </>
  )
}

export default ReportBankAndBCDataMatch